import { FC, useCallback, useMemo } from "react";
import { Stack } from "@mui/material";
import { DropdownSelect } from "@/components/common/select/dropdown-select/DropdownSelect";
import { DropdownSelectOption } from "@/components/common/select/dropdown-select/utils/types/types";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { dimensionsDataSelector } from "@/store/dimensions/selectors/dimensions-data/dimensionsDataSelector";
import { DimensionCategorySelectTriggerComponent } from "@/components/pages/dashboard/components/filters/dimension-category-filters/components/DimensionCategorySelectTriggerComponent";
import { updateDashboardDimensionFilterThunk } from "@/store/dashboards/thunks/dashboard/updateDashboardDimensionFilterThunk";
import { currentDashboardProtectedSelector } from "@/store/dashboards/selectors/dashboard-hierarchy-visibility/currentDashboardProtectedSelector";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";

interface DashboardCategoryIdSelectProps {
  selectedDimensionId: string;
  categoryId: string;
  dashboardId: string;
  onCategoryIdChange(id: string): void;
}

export const DashboardCategoryIdSelect: FC<DashboardCategoryIdSelectProps> = ({
  categoryId,
  selectedDimensionId,
  dashboardId,
  onCategoryIdChange,
}) => {
  const dispatch = useAppDispatch();
  const dimensions = useAppSelector(dimensionsDataSelector);
  const isProtected = useAppSelector(currentDashboardProtectedSelector);

  const renderOptions: DropdownSelectOption[] = useMemo(() => {
    const selectedDimension = dimensions?.find(
      (dimension) => dimension.id === selectedDimensionId,
    );

    return (
      selectedDimension?.categories?.map((option) => ({
        value: option.id,
        rawValue: option,
        label: (
          <Stack direction="row" alignItems="center" spacing={1} maxWidth={600}>
            <TypographyWithTooltip title={option.name} />
          </Stack>
        ),
      })) ?? []
    );
  }, [dimensions, selectedDimensionId]);

  const categorySelectHandler = useCallback(
    (values: string[]) => {
      const categoryId = values?.at(0);

      if (!categoryId) {
        return;
      }

      onCategoryIdChange(categoryId);

      dispatch(
        updateDashboardDimensionFilterThunk({
          dashboardId,
          category_id: categoryId,
          dimension_id: selectedDimensionId,
        }),
      );
    },
    [dispatch, selectedDimensionId, onCategoryIdChange, dashboardId],
  );

  return (
    <DropdownSelect
      singleSelect
      key={categoryId + selectedDimensionId}
      label="Category"
      filterFn={filterFn}
      values={categoryId ? [categoryId] : []}
      options={renderOptions}
      sortSelectedOptions={false}
      submitHandlerOnClose={categorySelectHandler}
      disabled={!selectedDimensionId || isProtected}
      TriggerComponent={DimensionCategorySelectTriggerComponent}
    />
  );
};

const filterFn = (option: DropdownSelectOption, keyword: string) => {
  return `${option.rawValue.name}`
    .toLowerCase()
    .includes(keyword.toLowerCase());
};
